/**
 * PaymentTimeline — Transforms payment Redux data into stages
 * and delegates rendering to the reusable LifecycleTracker.
 */
import { useAppSelector } from 'src/store/hooks';
import { LifecycleTracker, LifecycleStage, StepStatus } from 'src/components/common/LifecycleView';
import { EventsDetailPanel } from './EventsDetailPanel';
import { PaymentStatusDetail } from 'src/store/slices/payment/paymentState';

const STAGE_ORDER = [
  'RECEIVED', 'VALIDATION', 'TRANSFORMATION', 'OUTAGE', 'SEND_TO_BANK',
  'PAIN002_RECEIVED', 'PAIN002_PROCESSING',
  'CAMT053_RECEIVED', 'CAMT053_PROCESSING',
];

const mapState = (state: string): StepStatus => {
  if (state === 'SUCCESS' || state === 'NOT_HELD') return 'success';
  if (state === 'FAILED') return 'error';
  if (state === 'IN_PROGRESS') return 'loading';
  return 'pending';
};

export const PaymentTimeline = () => {
  const payment = useAppSelector(state => state.payment.data);

  if (!payment?.statusMap) return null;

  const statusMap = payment.statusMap;
  const presentStages = Object.keys(statusMap);
  const allStages = [
    ...STAGE_ORDER,
    ...presentStages.filter(s => !STAGE_ORDER.includes(s)),
  ];

  const stages: LifecycleStage[] = allStages.map((stage) => {
    const stageData: PaymentStatusDetail | undefined = statusMap[stage];
    const isReached = !!stageData;
    return {
      name: stage,
      status: isReached ? mapState(stageData.state) : 'pending',
      isReached,
      timestamp: stageData?.enteredAt ? new Date(stageData.enteredAt * 1000).toISOString() : undefined,
    };
  });

  const renderDetail = (stageName: string) => {
    const stageData = statusMap[stageName];
    if (!stageData?.statuses) return null;
    return <EventsDetailPanel stageName={stageName} events={stageData.statuses} />;
  };

  return (
    <LifecycleTracker
      title="Payment Lifecycle"
      stages={stages}
      renderDetail={renderDetail}
    />
  );
};
